import React, { useState, useMemo, useContext } from 'react';

import {
    Grid,
    TextField,
    CircularProgress,
    makeStyles,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
} from '@material-ui/core';
import { Field } from 'formik';
import InputMask from 'react-input-mask';
import zipApi from 'app/api/services/zipCodeEndpoints';
import generalEndpoints from 'app/api/services/generalEndpoints';
import _ from 'lodash';

import styles from './styles';
import { useSelector, useDispatch } from 'react-redux';
import { setCity } from 'app/store/slices/forms';
import { FormServiceContext } from 'app/utils/contexts';
import helpers from 'app/utils/helpers';
import Autocomplete from '@material-ui/lab/Autocomplete';

const useStyles = makeStyles(styles);

function Identification(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const request = useSelector((state) => state.requests.details);
    const service = useSelector((state) => state.services.details);
    const city = useSelector((state) => state.forms.city);

    const [phoneMask, setPhoneMask] = useState('(99) 9999-99999');

    const cepMask = '99999-999';
    const [documentMask, setDocumentMask] = useState('**.***.***/****-99');
    const [loadingAddress, setLoadingAddress] = useState(false);
    const [cities, setCities] = useState(() => {
        if (Object.keys(city).length > 0) return [city];
        return [];
    });
    const [loadingCity, setLoadingCity] = useState(false);
    const { values, errors, touched } = props;
    const { isInternalForm, editing, canEdit, user, blockDoc, blockEmail, blockPassword } =
        useContext(FormServiceContext);
    const [username] = useState(user?.nmUsuario ? user.nmUsuario : '');

    const [deTelefone, setDeTelefone] = useState(values.deTelefone ? values.deTelefone : '');

    const fieldsToHide = useMemo(() => {
        if (editing) {
            const fieldsHide = ['deSenha'];
            if (canEdit) {
                fieldsHide.push('deCpfCnpj');
            }
            return fieldsHide;
        }
        return [];
    }, [canEdit, editing]);

    const pessoaFisica = useMemo(() => {
        if (service.sgServico && service.sgServico === 'APF') {
            setDocumentMask('999.999.999-99');
            return true;
        } else if (request && request.servico?.sgServico === 'APF') {
            setDocumentMask('999.999.999-99');
            return true;
        }
        return false;
    }, [service.sgServico, request]);

    const searchAddress = async (e, values) => {
        setLoadingAddress(true);
        let cep = e.target.value;
        if (cep && cep !== '') {
            cep = cep.replace(/[^0-9]+/g, '');
            if (cep.length === 8) {
                const result = await zipApi.zipCodeSearch(cep);
                if (result) {
                    if (result.erro) {
                        result.cep = cep;
                        result.uf = '';
                        result.localidade = '';
                        result.bairro = '';
                        result.logradouro = '';
                        await dispatch(setCity({}));
                    }
                    values.deCep = result.cep;
                    values.uf = result.uf;
                    values.deBairro = result.bairro;
                    values.deEndereco = result.logradouro;
                    if (!result.erro) {
                        const response = await searchCity(values.uf);
                        const cityFound = _.find(response, ['nmMunicipio', result.localidade]);
                        if (cityFound) {
                            await onChangeMunicipio(null, cityFound);
                        }
                    }
                }
            }
        }
        setLoadingAddress(false);
    };

    const searchCity = async (uf) => {
        setLoadingCity(true);
        if (uf && uf !== '') {
            const response = await generalEndpoints.getCities(uf);
            setCities(response.data);
            setLoadingCity(false);
            return response.data;
        }
        setLoadingCity(false);
    };

    const onChangeMunicipio = async (event, value) => {
        if (value) {
            const temp = { ...value };
            values.municipio = temp;
            await dispatch(setCity(temp));
        } else {
            await dispatch(setCity({}));
            values.municipio = '';
        }
    };

    function validateDocument(value) {
        let error;
        if (!helpers.validateCpfCnpj(value)) {
            error = 'Documento inválido';
        }
        return error;
    }

    const changePhone = (e) => {
        const value = e.target.value;
        if (helpers.somenteNumeros(value).length >= 11) {
            setPhoneMask('(99) 99999-9999');
        } else {
            setPhoneMask('(99) 9999-99999');
        }
        setDeTelefone(value);
        values.deTelefone = value;
    };

    return (
        <Grid container spacing={3}>
            {isInternalForm && (
                <Grid item xs={12} sm={4}>
                    <TextField
                        name="usuarioResponsavel"
                        label="Usuário Responsável"
                        margin="dense"
                        fullWidth
                        disabled
                        value={username}
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </Grid>
            )}
            <Grid item xs={12} sm={4}>
                {canEdit && !blockDoc && !fieldsToHide.includes('deCpfCnpj') ? (
                    <Field name="deCpfCnpj" validate={validateDocument}>
                        {({ field, form: { errors, values } }) => (
                            <InputMask mask={documentMask} {...field} value={values?.deCpfCnpj}>
                                {(props) => (
                                    <TextField
                                        {...props}
                                        name="deCpfCnpj"
                                        label={pessoaFisica ? 'CPF*' : 'CNPJ*'}
                                        variant="filled"
                                        margin="dense"
                                        fullWidth
                                        error={!!errors.deCpfCnpj && touched.deCpfCnpj}
                                        helperText={errors.deCpfCnpj && touched.deCpfCnpj ? errors.deCpfCnpj : ''}
                                    />
                                )}
                            </InputMask>
                        )}
                    </Field>
                ) : (
                    <TextField
                        value={helpers.formatarDocumento(values?.deCpfCnpj)}
                        label={pessoaFisica ? 'CPF*' : 'CNPJ*'}
                        variant="filled"
                        margin="dense"
                        disabled
                        fullWidth
                    />
                )}
            </Grid>
            {(!fieldsToHide || !fieldsToHide.includes('nmUsuario')) && (
                <Grid item xs={12} sm={isInternalForm ? 4 : 8}>
                    <Field
                        name="nmUsuario"
                        label={pessoaFisica ? 'Nome*' : 'Razão Social*'}
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit}
                        fullWidth
                        error={!!errors.nmUsuario && touched.nmUsuario}
                        helperText={errors.nmUsuario && touched.nmUsuario ? errors.nmUsuario : ''}
                        as={TextField}
                        inputProps={{
                            maxLength: 90,
                        }}
                    />
                </Grid>
            )}

            {(!fieldsToHide || !fieldsToHide.includes('deCep')) && (
                <Grid item xs={12} sm={2} className={classes.positionRelative}>
                    {canEdit ? (
                        <Field name="deCep">
                            {({ field, form: { errors, values } }) => (
                                <InputMask
                                    mask={cepMask}
                                    {...field}
                                    value={values?.deCep}
                                    onBlur={(e) => searchAddress(e, values)}>
                                    {(props) => (
                                        <TextField
                                            {...props}
                                            name="deCep"
                                            label="CEP*"
                                            variant="filled"
                                            margin="dense"
                                            fullWidth
                                            error={!!errors.deCep && touched.deCep}
                                            helperText={errors.deCep && touched.deCep ? errors.deCep : ''}
                                            inputProps={{
                                                form: {
                                                    autocomplete: 'off',
                                                },
                                            }}
                                        />
                                    )}
                                </InputMask>
                            )}
                        </Field>
                    ) : (
                        <TextField
                            value={values?.deCep}
                            label="CEP*"
                            variant="filled"
                            margin="dense"
                            disabled
                            fullWidth
                        />
                    )}
                    {loadingAddress && (
                        <CircularProgress
                            className={classes.circularProgress}
                            disableShrink={true}
                            size={18}
                            thickness={6}
                        />
                    )}
                </Grid>
            )}
            {(!fieldsToHide || !fieldsToHide.includes('uf')) && (
                <Grid item xs={12} sm={2}>
                    <FormControl
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit}
                        fullWidth
                        error={!!errors.uf && touched.uf}>
                        <InputLabel htmlFor="uf">UF*</InputLabel>
                        <Field name="uf" id="uf" native as={Select} onBlur={(e) => searchCity(e.target.value)}>
                            <option aria-label="None" value="" />
                            <option value="AC">AC</option>
                            <option value="AL">AL</option>
                            <option value="AM">AM</option>
                            <option value="AP">AP</option>
                            <option value="BA">BA</option>
                            <option value="CE">CE</option>
                            <option value="DF">DF</option>
                            <option value="ES">ES</option>
                            <option value="GO">GO</option>
                            <option value="MA">MA</option>
                            <option value="MG">MG</option>
                            <option value="MS">MS</option>
                            <option value="MT">MT</option>
                            <option value="PA">PA</option>
                            <option value="PB">PB</option>
                            <option value="PE">PE</option>
                            <option value="PI">PI</option>
                            <option value="PR">PR</option>
                            <option value="RJ">RJ</option>
                            <option value="RN">RN</option>
                            <option value="RO">RO</option>
                            <option value="RR">RR</option>
                            <option value="RS">RS</option>
                            <option value="SC">SC</option>
                            <option value="SE">SE</option>
                            <option value="SP">SP</option>
                            <option value="TO">TO</option>
                        </Field>
                        {errors.uf && touched.uf ? <FormHelperText>{errors.uf}</FormHelperText> : null}
                    </FormControl>
                </Grid>
            )}
            {(!fieldsToHide || !fieldsToHide.includes('municipio')) && (
                <Grid item xs={12} sm={4}>
                    <Field name="municipio">
                        {({ form: { errors } }) => {
                            return (
                                <div className={classes.positionRelative}>
                                    <Autocomplete
                                        label="Município*"
                                        id="municipio"
                                        options={cities}
                                        getOptionLabel={(option) =>
                                            option['nmMunicipio'] ? option['nmMunicipio'] : ''
                                        }
                                        disabled={!canEdit}
                                        onChange={onChangeMunicipio}
                                        value={city}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Município*"
                                                fullWidth
                                                variant="filled"
                                                margin="dense"
                                                error={!!errors.municipio && touched.municipio}
                                                helperText={
                                                    errors.municipio && touched.municipio ? errors.municipio : ''
                                                }
                                            />
                                        )}
                                    />
                                    {loadingCity && (
                                        <CircularProgress
                                            className={classes.circularProgress}
                                            disableShrink={true}
                                            size={18}
                                            thickness={6}
                                        />
                                    )}
                                </div>
                            );
                        }}
                    </Field>
                </Grid>
            )}
            {(!fieldsToHide || !fieldsToHide.includes('deBairro')) && (
                <Grid item xs={12} sm={4}>
                    <Field
                        name="deBairro"
                        label="Bairro*"
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit}
                        fullWidth
                        error={!!errors.deBairro && touched.deBairro}
                        helperText={errors.deBairro && touched.deBairro ? errors.deBairro : ''}
                        value={values?.deBairro ? values.deBairro : ''}
                        as={TextField}
                        inputProps={{
                            maxLength: 50,
                        }}
                    />
                </Grid>
            )}
            {(!fieldsToHide || !fieldsToHide.includes('deEndereco')) && (
                <Grid item xs={12} sm={6}>
                    <Field
                        name="deEndereco"
                        label="Endereço*"
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit}
                        fullWidth
                        error={!!errors.deEndereco && touched.deEndereco}
                        helperText={errors.deEndereco && touched.deEndereco ? errors.deEndereco : ''}
                        value={values?.deEndereco ? values.deEndereco : ''}
                        as={TextField}
                        inputProps={{
                            maxLength: 100,
                        }}
                    />
                </Grid>
            )}
            {(!fieldsToHide || !fieldsToHide.includes('deNumeroEndereco')) && (
                <Grid item xs={12} sm={2}>
                    <Field
                        name="deNumeroEndereco"
                        label="Número"
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit}
                        fullWidth
                        error={!!errors.deNumeroEndereco && touched.deNumeroEndereco}
                        helperText={errors.deNumeroEndereco && touched.deNumeroEndereco ? errors.deNumeroEndereco : ''}
                        value={values?.deNumeroEndereco ? values.deNumeroEndereco : ''}
                        as={TextField}
                        inputProps={{
                            maxLength: 10,
                        }}
                    />
                </Grid>
            )}
            {(!fieldsToHide || !fieldsToHide.includes('deComplemento')) && (
                <Grid item xs={12} sm={4}>
                    <Field
                        name="deComplemento"
                        label="Complemento"
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit}
                        fullWidth
                        value={values?.deComplemento ? values.deComplemento : ''}
                        as={TextField}
                        inputProps={{
                            maxLength: 50,
                        }}
                    />
                </Grid>
            )}
            <Grid item xs={12} sm={4}>
                <Field
                    name="deEmail"
                    label="E-mail*"
                    variant="filled"
                    margin="dense"
                    disabled={!canEdit || fieldsToHide.includes('deEmail') || blockEmail}
                    fullWidth
                    error={!!errors.deEmail && touched.deEmail}
                    helperText={errors.deEmail && touched.deEmail ? errors.deEmail : ''}
                    as={TextField}
                    inputProps={{
                        maxLength: 90,
                    }}
                />
            </Grid>
            {!isInternalForm && !blockPassword && (!fieldsToHide || !fieldsToHide.includes('deSenha')) && (
                <Grid item xs={12} sm={4}>
                    <Field
                        name="deSenha"
                        label="Senha*"
                        type="password"
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit || blockPassword}
                        fullWidth
                        error={!!errors.deSenha && touched.deSenha}
                        helperText={errors.deSenha && touched.deSenha ? errors.deSenha : ''}
                        as={TextField}
                        inputProps={{
                            maxLength: 20,
                        }}
                    />
                </Grid>
            )}
            {(!fieldsToHide || !fieldsToHide.includes('deTelefone')) && (
                <Grid item xs={12} sm={4}>
                    {canEdit ? (
                        <Field name="deTelefone">
                            {({ field, form: { touched, errors } }) => (
                                <InputMask
                                    mask={phoneMask}
                                    maskChar={' '}
                                    {...field}
                                    onChange={(e) => changePhone(e)}
                                    value={deTelefone}>
                                    {() => (
                                        <TextField
                                            {...props}
                                            name="deTelefone"
                                            label="Telefone*"
                                            variant="filled"
                                            margin="dense"
                                            fullWidth
                                            error={!!errors.deTelefone && touched.deTelefone}
                                            helperText={
                                                errors.deTelefone && touched.deTelefone ? errors.deTelefone : ''
                                            }
                                            inputProps={{
                                                form: {
                                                    autocomplete: 'off',
                                                },
                                            }}
                                        />
                                    )}
                                </InputMask>
                            )}
                        </Field>
                    ) : (
                        <TextField
                            label="Telefone*"
                            variant="filled"
                            margin="dense"
                            fullWidth
                            value={values?.deTelefone}
                            disabled
                        />
                    )}
                </Grid>
            )}
            {(!fieldsToHide || !fieldsToHide.includes('deObservacao')) && (
                <Grid item xs={12}>
                    <Field
                        name="deObservacao"
                        label="Observações"
                        variant="filled"
                        margin="dense"
                        disabled={!canEdit}
                        multiline
                        minRows={4}
                        fullWidth
                        as={TextField}
                        inputProps={{
                            maxLength: 4900,
                        }}
                    />
                </Grid>
            )}
        </Grid>
    );
}

export default Identification;
